import React, { useCallback, useEffect, useRef } from "react";
import { apiDomain } from "configs/configs";
import {
  Flex,
  Input,
  Form,
  Button,
  Grid,
  Upload,
  Modal,
  UploadFile,
} from "antd";
import { Modal as MobileModal } from "antd-mobile";
import { UploadOutlined } from "@ant-design/icons";
import { UploadChangeParam } from "antd/es/upload/interface";
import { create } from "zustand";
import { MeasureType } from "pages/admin/objectsAndClients/objects/types";

const { useBreakpoint } = Grid;

type ComponentProps = {
  measures: MeasureType[];
  setMeasures: (update: (state: MeasureType[]) => MeasureType[]) => void;
  isAddBuildingMode: boolean;
  setIsAddBuildingMode: (isAddBuildingMode: boolean) => void;
  isEditBuildingMode: string | undefined;
  setIsEditBuildingMode: (isEditBuildingMode?: string) => void;
};

type OperationsStoreState = {
  isFileUploading: boolean;
  setIsFileUploading: (isError: boolean) => void;
  previewFile?: UploadFile;
  setPreviewFile: (previewFile?: UploadFile) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
};

const useOperationsStore = create<OperationsStoreState>((set) => ({
  isFileUploading: false,
  setIsFileUploading: (isFileUploading) => set({ isFileUploading }),
  previewFile: undefined,
  setPreviewFile: (previewFile?: UploadFile) => set({ previewFile }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
}));

const Component: React.FC<ComponentProps> = ({
  measures,
  setMeasures,
  isAddBuildingMode,
  setIsAddBuildingMode,
  isEditBuildingMode,
  setIsEditBuildingMode,
}) => {
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const measuresRef = useRef(measures);

  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    measuresRef.current = measures;
  }, [measures]);

  const {
    isFileUploading,
    setIsFileUploading,
    previewFile,
    setPreviewFile,
    isSubmittable,
    setIsSubmittable,
  } = useOperationsStore((store) => store);

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const item = measuresRef.current.find(
      (item) => item.id.toString() === isEditBuildingMode
    );

    if (!item) form.resetFields();

    if (item) {
      const file = item.user_file
        ? [
            {
              uid: item.user_file.id,
              name: item.user_file.file_name,
              size: item.user_file.size,
              type: item.user_file.mime_type,
              response: [
                {
                  file_name: item.user_file.file_name,
                  file_path: item.user_file.file_path,
                  mime_type: item.user_file.mime_type,
                  size: item.user_file.size,
                  id: item.user_file.id,
                },
              ],
              thumbUrl: `${apiDomain}/userFiles/${item.user_file.file_path}`,
            },
          ]
        : [];

      form.setFieldsValue({
        name: item.name,
        file: file,
      });
    }

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isEditBuildingMode, form, measuresRef, setIsSubmittable]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [form, values, setIsSubmittable]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    try {
      const name = values.name.trim();
      const fileId = values?.file?.[0]?.response?.[0]?.id
        ? values?.file?.[0]?.response[0].id
        : values?.file?.[0]?.uid && !isNaN(Number(values?.file?.[0].uid))
        ? Number(values?.file?.[0].uid)
        : null;

      const userFile = fileId
        ? {
            id: fileId,
            file_name: values?.file?.[0]?.response?.[0]?.file_name
              ? values?.file?.[0]?.response[0].file_name
              : values?.file?.[0]?.name
              ? values?.file?.[0].name
              : null,
            file_path: values?.file?.[0]?.response?.[0]?.file_path
              ? values?.file?.[0]?.response[0].file_path
              : null,
            mime_type: values?.file?.[0]?.response?.[0]?.mime_type
              ? values?.file?.[0]?.response[0].mime_type
              : values?.file?.[0]?.type
              ? values?.file?.[0].type
              : null,
            size: values?.file?.[0]?.response?.[0]?.size
              ? values?.file?.[0]?.response[0].size
              : values?.file?.[0]?.size
              ? values?.file?.[0].size
              : null,
          }
        : undefined;

      setMeasures((measures) => {
        if (isEditBuildingMode) {
          const id = isEditBuildingMode;

          return measures.map((measure) =>
            measure.id.toString() === id
              ? {
                  ...measure,
                  name: name,
                  user_file_id: fileId,
                  user_file: userFile,
                }
              : measure
          );
        }

        const maxId = measures
          .filter((measure) =>
            measure.id.toString()?.startsWith("newBuilding-")
          )
          .map((measure) => parseInt(measure.id.toString().split("-")[1], 10))
          .reduce((max, current) => Math.max(max, current), 0);

        const newId = `newBuilding-${maxId + 1}`;

        return [
          ...measures,
          {
            id: newId,
            name: name,
            user_file_id: fileId,
            user_file: userFile,
            is_children_visible: true,
          },
        ];
      });
    } catch (error) {
      console.log(error);
    }

    if (isEditBuildingMode) setIsEditBuildingMode(undefined);
    if (isAddBuildingMode) setIsAddBuildingMode(false);
  }, [
    setMeasures,
    values,
    isAddBuildingMode,
    setIsAddBuildingMode,
    isEditBuildingMode,
    setIsEditBuildingMode,
  ]);

  const handleUploadFileChange = useCallback(
    (info: UploadChangeParam) => {
      setIsFileUploading(info.file.status === "uploading");
    },
    [setIsFileUploading]
  );

  const handlePreview = useCallback(
    async (previewFile: UploadFile) => {
      setPreviewFile(previewFile);
    },
    [setPreviewFile]
  );

  return (
    <>
      <Flex vertical gap={16}>
        <Form
          layout={"vertical"}
          form={form}
          onFinish={handleFinish}
          initialValues={{
            name: "",
            file: null,
          }}
        >
          <Form.Item
            name="name"
            label="Наименование"
            rules={[
              {
                required: true,
                message: "Пожалуйста, введите наименование!",
              },
            ]}
            validateTrigger={false}
            required={false}
          >
            <Input size="large" name="name" />
          </Form.Item>
          <Form.Item
            label="Добавить фото или чертеж"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload
              action={`${apiDomain}/api/upload-file`}
              accept="image/*"
              maxCount={1}
              withCredentials={true}
              onChange={handleUploadFileChange}
              onPreview={handlePreview}
              listType="picture-card"
            >
              <Button icon={<UploadOutlined />} disabled={isFileUploading}>
                {isFileUploading ? `Идет загрузка` : `Загрузить файл`}
              </Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              size="large"
              disabled={!isSubmittable || isFileUploading}
              htmlType="submit"
            >
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Flex>
      {isMobile && (
        <MobileModal
          visible={!!previewFile?.url || !!previewFile?.thumbUrl}
          content={
            <img
              alt={previewFile?.fileName}
              style={{ width: "100%" }}
              src={
                previewFile?.url
                  ? previewFile?.url
                  : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                  : previewFile?.thumbUrl
              }
              onClick={() => setPreviewFile(undefined)}
            />
          }
          closeOnMaskClick={true}
          showCloseButton={true}
          onClose={() => setPreviewFile(undefined)}
        />
      )}
      {!isMobile && (
        <Modal
          open={!!previewFile?.url || !!previewFile?.thumbUrl}
          title={previewFile?.fileName}
          footer={null}
          onCancel={() => setPreviewFile(undefined)}
        >
          <img
            alt={previewFile?.fileName}
            style={{ width: "100%" }}
            src={
              previewFile?.url
                ? previewFile?.url
                : `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                ? `${apiDomain}/userFiles/${previewFile?.response?.[0]?.file_path}`
                : previewFile?.thumbUrl
            }
            onClick={() => setPreviewFile(undefined)}
          />
        </Modal>
      )}
    </>
  );
};

export default Component;
